import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';

import { ComboBoxField } from '@epoch-ui/components/Form/fields/ComboBoxField/ComboBoxField';
import { Skeleton } from '@epoch-ui/components/Skeleton/skeleton';
import { SlackWorkspace } from 'graphql/queries/company-slack/company-workspaces';
import { companySlackApi } from 'api';

import { SlackSettingsFormValues } from './slackSettingsSchema';

const SlackWorkspaceError = React.memo(() => (
  <div className="tw-mb-4 tw-flex tw-flex-col">
    <label className="tw-mb-2 tw-text-sm tw-font-medium tw-text-dark-black">Slack Workspace</label>
    <div className="tw-rounded-md tw-border tw-border-error-red tw-bg-error-red/5 tw-p-3 tw-text-sm tw-text-error-red">
      Failed to load Slack workspaces. Please try again.
    </div>
  </div>
));

const SlackWorkspaceLoading = React.memo(() => (
  <div className="tw-mb-4 tw-flex tw-flex-col">
    <label className="tw-mb-2 tw-text-sm tw-font-medium tw-text-dark-black">Slack Workspace</label>
    <div className="tw-flex tw-min-h-[40px] tw-flex-col tw-gap-2 tw-rounded-md tw-border tw-border-input tw-px-3 tw-py-2">
      {Array(5)
        .fill(0)
        .map((_, i) => (
          <Skeleton key={i} className="tw-h-5 tw-w-full tw-rounded-sm" />
        ))}
    </div>
  </div>
));

interface SlackWorkspaceSelectorProps {
  disabled?: boolean;
}

export const SlackWorkspaceSelector: React.FC<SlackWorkspaceSelectorProps> = ({
  disabled = false
}) => {
  const { reset, getValues } = useFormContext<SlackSettingsFormValues>();

  // Fetch workspaces using TanStack Query
  const {
    data: workspaceData,
    isLoading: isLoadingWorkspaces,
    error: workspacesError
  } = useQuery({
    queryKey: ['dashboard', 'editEventSettings', 'companySlackWorkspaces'],
    queryFn: companySlackApi.listWorkspaces,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2
  });

  const companySlackWorkspaces = workspaceData?.workspaces || [];

  const handleWorkspaceSelect = (teamId: string): void => {
    const currentValues = getValues();
    reset({
      ...currentValues,
      slackTeamId: teamId,
      slackChannelId: undefined,
      slackChannelName: ''
    });
  };

  const getSelectedWorkspaceName = useCallback(
    (teamId: string): React.ReactNode => {
      if (!teamId) return 'Select workspace';
      const workspace = companySlackWorkspaces.find((w) => w.teamId === teamId);
      return (
        <span className="tw-text-sm tw-text-dark-black">
          {workspace?.name || 'Select workspace'}
        </span>
      );
    },
    [companySlackWorkspaces]
  );

  if (workspacesError) {
    return <SlackWorkspaceError />;
  }

  if (isLoadingWorkspaces) {
    return <SlackWorkspaceLoading />;
  }

  return (
    <div className="tw-mb-4">
      <ComboBoxField
        name="slackTeamId"
        label="Slack Workspace"
        triggerContent={getSelectedWorkspaceName}
        disabled={disabled}
      >
        <ComboBoxField.Command>
          <ComboBoxField.List className="tw-mt-1">
            {!companySlackWorkspaces?.length ? (
              <ComboBoxField.Empty>No workspaces available</ComboBoxField.Empty>
            ) : (
              companySlackWorkspaces.map((workspace: SlackWorkspace) => (
                <ComboBoxField.Item
                  key={workspace.teamId}
                  value={workspace.teamId}
                  onSelect={handleWorkspaceSelect}
                >
                  <span className="tw-overflow-hidden tw-truncate tw-whitespace-nowrap tw-text-sm tw-font-normal tw-leading-5 tw-text-dark-black">
                    {workspace.name}
                  </span>
                </ComboBoxField.Item>
              ))
            )}
          </ComboBoxField.List>
        </ComboBoxField.Command>
      </ComboBoxField>
    </div>
  );
};
